import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import "./ImageViewer.css";

const CloseSvg = () => (
  <svg
    aria-hidden="true"
    focusable="false"
    fill="none"
    width="16"
    height="16"
    className="icon icon-block"
    viewBox="0 0 16 16"
  >
    <path
      d="m1 1 14 14M1 15 15 1"
      stroke="currentColor"
      strokeWidth="1"
    />
  </svg>
);

const ChevronPrevSvg = () => (
  <svg
    aria-hidden="true"
    focusable="false"
    fill="none"
    width="16"
    height="18"
    className="icon icon-inline icon--direction-aware"
    viewBox="0 0 16 18"
  >
    <path
      d="M11 1 3 9l8 8"
      stroke="currentColor"
      strokeLinecap="square"
    />
  </svg>
);

const ChevronNextSvg = () => (
  <svg
    aria-hidden="true"
    focusable="false"
    fill="none"
    width="16"
    height="18"
    className="icon icon-inline icon--direction-aware"
    viewBox="0 0 16 18"
  >
    <path
      d="m5 17 8-8-8-8"
      stroke="currentColor"
      strokeLinecap="square"
    />
  </svg>
);

/**
 * Higher FILL_FRACTION ⇒ gentler zoom (more of the product stays visible — clearer context).
 */
const FILL_FRACTION = 0.52;
const ZOOM_MIN = 1.18;
const ZOOM_MAX = 2.65;
const FIT_EPS = 0.015;

function computeTargetZoom(frameW, frameH, imgW, imgH) {
  if (!Number.isFinite(frameW) || !Number.isFinite(frameH) || frameW < 8 || frameH < 8) {
    return 1.72;
  }
  if (!Number.isFinite(imgW) || !Number.isFinite(imgH) || imgW < 4 || imgH < 4) {
    return 1.72;
  }
  const zx = frameW / (imgW * FILL_FRACTION);
  const zy = frameH / (imgH * FILL_FRACTION);
  const z = Math.max(zx, zy);
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));
}

const clampPct = (n) =>
  Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : 50;

/** @returns {{ cx: number; cy: number } | null} */
const getClientPoint = (e) => {
  if (!e || typeof e !== "object") return null;
  if (typeof e.clientX === "number" && !Number.isNaN(e.clientX)) {
    return { cx: e.clientX, cy: e.clientY };
  }
  const ne = e.nativeEvent;
  const t = ne?.changedTouches?.[0] ?? ne?.touches?.[0];
  if (t && typeof t.clientX === "number") {
    return { cx: t.clientX, cy: t.clientY };
  }
  return null;
};

const ImageViewer = ({
  images = [],
  currentIndex = 0,
  isVisible = false,
  onClose,
  onNext,
  onPrevious,
}) => {
  const frameRef = useRef(null);
  const imgRef = useRef(null);
  const lastZoomGestureAt = useRef(0);
  const zoomRef = useRef(1);
  const pinExpandRef = useRef(false);
  const [zoom, setZoom] = useState(1);
  const [originPct, setOriginPct] = useState({ x: 50, y: 50 });
  const [frozenBox, setFrozenBox] = useState(null);
  /** One frame: pinned fitted size → then zoom animates up (smooth width/height). */
  const [pinExpand, setPinExpand] = useState(false);
  const expandTargetRef = useRef(null);
  const scrollFallbackTimerRef = useRef(0);

  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  useEffect(() => {
    pinExpandRef.current = pinExpand;
  }, [pinExpand]);

  useEffect(() => {
    setZoom(1);
    zoomRef.current = 1;
    setOriginPct({ x: 50, y: 50 });
    setFrozenBox(null);
    setPinExpand(false);
    expandTargetRef.current = null;
    pinExpandRef.current = false;
    window.clearTimeout(scrollFallbackTimerRef.current);
  }, [currentIndex, isVisible]);

  useEffect(() => {
    if (zoom > 1 + FIT_EPS) return;
    const el = frameRef.current;
    if (el) {
      el.scrollTop = 0;
      el.scrollLeft = 0;
    }
  }, [zoom]);

  useEffect(() => {
    if (!isVisible) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isVisible]);

  useEffect(() => {
    return () => {
      window.clearTimeout(scrollFallbackTimerRef.current);
    };
  }, []);

  const applyZoomFit = useCallback(() => {
    expandTargetRef.current = null;
    pinExpandRef.current = false;
    window.clearTimeout(scrollFallbackTimerRef.current);
    setPinExpand(false);
    setFrozenBox(null);
    setOriginPct({ x: 50, y: 50 });
    setZoom(1);
    zoomRef.current = 1;
  }, []);

  useEffect(() => {
    if (!isVisible) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (pinExpandRef.current || zoomRef.current > 1 + FIT_EPS) {
          applyZoomFit();
          return;
        }
        onClose?.();
        return;
      }
      if (images.length <= 1) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        onPrevious?.();
        return;
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        onNext?.();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isVisible, images.length, onClose, onNext, onPrevious, applyZoomFit]);

  const computeOriginFromPointer = useCallback((e) => {
    const imgEl = imgRef.current;
    if (!imgEl) return { x: 50, y: 50 };
    const rect = imgEl.getBoundingClientRect();
    if (rect.width < 4 || rect.height < 4) return { x: 50, y: 50 };
    const pt = getClientPoint(e);
    if (!pt) return { x: 50, y: 50 };
    return {
      x: clampPct(((pt.cx - rect.left) / rect.width) * 100),
      y: clampPct(((pt.cy - rect.top) / rect.height) * 100),
    };
  }, []);

  const scrollFocalIntoFrame = useCallback(() => {
    const frame = frameRef.current;
    if (!frame || !frozenBox) return;
    const z = zoomRef.current;
    if (z <= 1 + FIT_EPS) return;
    const iw = frozenBox.w * z;
    const ih = frozenBox.h * z;
    const fx = (originPct.x / 100) * iw;
    const fy = (originPct.y / 100) * ih;
    const maxLeft = Math.max(0, iw - frame.clientWidth);
    const maxTop = Math.max(0, ih - frame.clientHeight);
    frame.scrollLeft = Math.min(maxLeft, Math.max(0, fx - frame.clientWidth / 2));
    frame.scrollTop = Math.min(maxTop, Math.max(0, fy - frame.clientHeight / 2));
  }, [frozenBox, originPct]);

  useLayoutEffect(() => {
    if (!pinExpand || !frozenBox) return;
    const tgt = expandTargetRef.current;
    if (tgt == null || !Number.isFinite(tgt)) {
      pinExpandRef.current = false;
      setPinExpand(false);
      return;
    }
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        expandTargetRef.current = null;
        zoomRef.current = tgt;
        pinExpandRef.current = false;
        setZoom(tgt);
        setPinExpand(false);
        window.clearTimeout(scrollFallbackTimerRef.current);
        scrollFallbackTimerRef.current = window.setTimeout(() => {
          scrollFocalIntoFrame();
        }, 560);
      });
    });
    return () => cancelAnimationFrame(id);
  }, [pinExpand, frozenBox, scrollFocalIntoFrame]);

  const onZoomDimensionTransitionEnd = useCallback(
    (e) => {
      if (zoomRef.current <= 1 + FIT_EPS) return;
      if (e.propertyName !== "width" && e.propertyName !== "height") return;
      window.clearTimeout(scrollFallbackTimerRef.current);
      scrollFocalIntoFrame();
    },
    [scrollFocalIntoFrame]
  );

  const cycleZoom = useCallback(
    (e) => {
      if (e && typeof e.stopPropagation === "function") {
        e.stopPropagation();
      }
      const now = Date.now();
      if (now - lastZoomGestureAt.current < 280) {
        return;
      }
      lastZoomGestureAt.current = now;

      if (zoomRef.current > 1 + FIT_EPS) {
        applyZoomFit();
        return;
      }

      const imgEl = imgRef.current;
      if (!imgEl) return;
      const r = imgEl.getBoundingClientRect();
      if (r.width < 4 || r.height < 4) return;

      const fw = frameRef.current?.clientWidth ?? 0;
      const fh = frameRef.current?.clientHeight ?? 0;
      const target = computeTargetZoom(fw, fh, r.width, r.height);

      setFrozenBox({ w: r.width, h: r.height });
      setOriginPct(computeOriginFromPointer(e));
      expandTargetRef.current = target;
      zoomRef.current = 1;
      setZoom(1);
      pinExpandRef.current = true;
      setPinExpand(true);
    },
    [applyZoomFit, computeOriginFromPointer]
  );

  const handleBackdropClick = (e) => {
    if (e.target.closest(".gallery-pswp__toolbar")) return;
    if (e.target.closest(".gallery-pswp__img")) return;
    onClose?.();
  };

  if (!isVisible || !images?.length) return null;

  const multi = images.length > 1;
  const src = images[currentIndex];
  const inZoomChrome = !!frozenBox && (pinExpand || zoom > 1 + FIT_EPS);
  const zoomedIn = frozenBox != null && zoom > 1 + FIT_EPS && !pinExpand;

  const imgStyle = {
    cursor: zoomedIn ? "zoom-out" : "zoom-in",
    ...(inZoomChrome
      ? {
          width: frozenBox.w * zoom,
          height: frozenBox.h * zoom,
        }
      : {}),
  };

  let imgZoomClassName = "gallery-pswp__img";
  if (inZoomChrome) {
    imgZoomClassName += " gallery-pswp__img--zoomed";
    if (!(pinExpand || zoom <= 1 + FIT_EPS)) {
      imgZoomClassName += " gallery-pswp__img--zoom-animate";
    }
  }

  return createPortal(
    <div
      className="gallery-pswp"
      role="dialog"
      aria-modal="true"
      aria-label="Image gallery"
      onClick={handleBackdropClick}
    >
      <div className="gallery-pswp__inner">
        <div className="gallery-pswp__stage">
          <div
            ref={frameRef}
            className={
              inZoomChrome
                ? "gallery-pswp__frame gallery-pswp__frame--zoom"
                : "gallery-pswp__frame"
            }
          >
            <div
              className={
                inZoomChrome
                  ? "gallery-pswp__viewport gallery-pswp__viewport--zoom"
                  : "gallery-pswp__viewport"
              }
            >
              {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- tap zoom */}
              <img
                ref={imgRef}
                src={src}
                alt=""
                className={imgZoomClassName}
                role="button"
                tabIndex={0}
                aria-label={
                  zoomedIn
                    ? "Zoom out"
                    : "Zoom in on tapped area"
                }
                style={imgStyle}
                onTransitionEnd={onZoomDimensionTransitionEnd}
                onClick={(ev) => cycleZoom(ev)}
                onKeyDown={(ev) => {
                  if (ev.key === "Enter" || ev.key === " ") {
                    ev.preventDefault();
                    cycleZoom(ev);
                  }
                }}
                draggable={false}
              />
            </div>
          </div>

          <div
            className="gallery-pswp__toolbar"
            role="toolbar"
            aria-label="Gallery controls"
          >
            <button
              type="button"
              disabled={!multi}
              aria-disabled={!multi}
              aria-label="Previous image"
              className="pswp__button circle-button circle-button--prev"
              onClick={(ev) => {
                ev.stopPropagation();
                multi && onPrevious?.();
              }}
            >
              <ChevronPrevSvg />
            </button>
            <button
              type="button"
              className="pswp__button circle-button circle-button--close"
              aria-label="Close gallery"
              onClick={(ev) => {
                ev.stopPropagation();
                onClose?.();
              }}
            >
              <CloseSvg />
            </button>
            <button
              type="button"
              disabled={!multi}
              aria-disabled={!multi}
              aria-label="Next image"
              className="pswp__button circle-button circle-button--next"
              onClick={(ev) => {
                ev.stopPropagation();
                multi && onNext?.();
              }}
            >
              <ChevronNextSvg />
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ImageViewer;
